export const ANALYTICS_EVENT_TYPES = {
    PAGE_VIEW: 'page_view',
    PAGE_ENGAGEMENT: 'page_engagement',
    VIDEO_WATCH_START: 'video_watch_start',
    VIDEO_WATCH_PROGRESS: 'video_watch_progress',
    VIDEO_WATCH_COMPLETE: 'video_watch_complete',
    VIDEO_SAVED: 'video_saved',
    VIDEO_UNSAVED: 'video_unsaved',
    VIDEO_PURCHASED: 'video_purchased',
    AUTH_LOGIN_SUCCESS: 'auth_login_success',
    AUTH_LOGIN_FAILED: 'auth_login_failed',
    AUTH_MODAL_OPENED: 'auth_modal_opened',
    SIGNUP_STARTED: 'signup_started',
    SIGNUP_COMPLETED: 'signup_completed',
    SIGNUP_FAILED: 'signup_failed',
    SUBSCRIPTION_PRICING_VIEWED: 'subscription_pricing_viewed',
    SUBSCRIPTION_PLAN_SELECTED: 'subscription_plan_selected',
    SUBSCRIPTION_CHECKOUT_STARTED: 'subscription_checkout_started',
    SUBSCRIPTION_CHECKOUT_VIEWED: 'subscription_checkout_viewed',
    SUBSCRIPTION_COMPLETED: 'subscription_completed',
    SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
    SUBSCRIPTION_PORTAL_OPENED: 'subscription_portal_opened',
    MEMBERSHIP_CANCELLED: 'membership_cancelled',
    SEARCH_PERFORMED: 'search_performed',
    SEARCH_EMPTY: 'search_empty',
    SEARCH_RESULT_CLICKED: 'search_result_clicked',
    FILTER_APPLIED: 'filter_applied',
    COLLECTION_VIEWED: 'collection_viewed',
    TAG_CLICKED: 'tag_clicked',
    FIGHTER_CLICKED: 'fighter_clicked',
    EVENT_CLICKED: 'event_clicked',
    CONTINUE_WATCHING_CLICKED: 'continue_watching_clicked',
    SUGGESTED_NEXT_CLICKED: 'suggested_next_clicked',
    RELATED_VIDEO_CLICKED: 'related_video_clicked',
    VIDEO_CARD_CLICKED: 'video_card_clicked',
    VIDEO_PLAYBACK_STARTUP: 'video_playback_startup',
    VIDEO_PLAYBACK_ERROR: 'video_playback_error',
    VIDEO_BUFFERING: 'video_buffering',
    VIDEO_PAUSED: 'video_paused',
    VIDEO_RESUMED: 'video_resumed',
    VIDEO_SEEKED: 'video_seeked',
    VIDEO_RESTARTED: 'video_restarted',
    PURCHASE_CHECKOUT_STARTED: 'purchase_checkout_started',
    CHECKOUT_ABANDONED: 'checkout_abandoned',
    CONTACT_SUBMITTED: 'contact_submitted'
} as const;

export type AnalyticsEventType = typeof ANALYTICS_EVENT_TYPES[keyof typeof ANALYTICS_EVENT_TYPES];

/**
 * Browser-origin analytics payload. Values must stay safe for first-party analytics storage.
 */
export interface AnalyticsEventRequest {
    eventType: AnalyticsEventType;
    clientEventId?: string;
    clientOccurredAtUtc?: string;
    anonymousId?: string;
    path?: string;
    referrer?: string | null;
    sessionId?: string;
    landingPage?: string | null;
    utmSource?: string | null;
    utmMedium?: string | null;
    utmCampaign?: string | null;
    utmTerm?: string | null;
    utmContent?: string | null;
    gclid?: string | null;
    gbraid?: string | null;
    wbraid?: string | null;
    fbclid?: string | null;
    fbp?: string | null;
    fbc?: string | null;
    videoId?: string;
    positionSeconds?: number;
    durationSeconds?: number;
    value?: number;
    currency?: string | null;
    orderId?: string | null;
    metadata?: Record<string, string | number | boolean | null | undefined>;
}

export interface AnalyticsDailyCount {
    date: string;
    count: number;
}

export interface AnalyticsVideoCount {
    videoId: string;
    title: string;
    count: number;
}

export interface AnalyticsLabelCount {
    label: string;
    count: number;
    uniqueUsers: number;
}

export interface AnalyticsEventTypeCount {
    eventType: string;
    count: number;
    uniqueUsers: number;
}

export interface AnalyticsCampaignCount {
    source: string;
    medium?: string | null;
    campaign?: string | null;
    count: number;
    uniqueUsers: number;
}

export interface AnalyticsRetentionSummary {
    activeUsers: number;
    newUsers: number;
    returningUsers: number;
    repeatUsers: number;
    averageActiveDays: number;
    dailyActiveUsers: AnalyticsDailyCount[];
}

export interface AnalyticsRecentActivity {
    occurredAtUtc: string;
    eventType: string;
    userName?: string | null;
    videoTitle?: string | null;
    path?: string | null;
    summary?: string | null;
}

export interface AdvertisingConversionSummary {
    provider: string;
    eventName: string;
    status: string;
    count: number;
}

export interface AnalyticsLoginAudit {
    occurredAtUtc: string;
    userName?: string | null;
    isSuccess: boolean;
    ipAddress?: string | null;
    userAgent?: string | null;
}

export interface AnalyticsVideoAudit {
    occurredAtUtc: string;
    videoId: string;
    title: string;
    userName?: string | null;
}

/**
 * Playback timing and error summary for a requested quality/transport bucket.
 */
export interface AnalyticsPlaybackPerformanceBucket {
    label: string;
    startupSampleCount: number;
    averageStartupMs: number;
    bufferEventCount: number;
    averageBufferMs: number;
    seekSampleCount: number;
    averageSeekMs: number;
    playbackErrorCount: number;
}

/**
 * Playback timing and file-state summary for one video/quality/transport row.
 */
export interface AnalyticsVideoPlaybackPerformance {
    videoId: string;
    videoShortId: string;
    videoTitle: string;
    videoStatus: string;
    publishStatus: string;
    quality: string;
    transport: string;
    bucketLabel: string;
    fileFormat: string;
    fileStatus?: string | null;
    filePath?: string | null;
    fileSizeBytes?: number | null;
    startupSampleCount: number;
    averageStartupMs: number;
    bufferEventCount: number;
    averageBufferMs: number;
    seekSampleCount: number;
    averageSeekMs: number;
    playbackErrorCount: number;
    lastPlayedAtUtc: string;
}

/**
 * Playback timing and error summary for the selected analytics range.
 */
export interface AnalyticsPlaybackPerformance {
    startupSampleCount: number;
    averageStartupMs: number;
    bufferEventCount: number;
    averageBufferMs: number;
    seekSampleCount: number;
    averageSeekMs: number;
    playbackErrorCount: number;
    buckets: AnalyticsPlaybackPerformanceBucket[];
    videos: AnalyticsVideoPlaybackPerformance[];
}

/**
 * SuperAdministrator playback performance report for a current period and comparison period.
 */
export interface AnalyticsPlaybackPerformanceReport {
    rangeStartUtc: string;
    rangeEndUtc: string;
    comparisonRangeStartUtc: string;
    comparisonRangeEndUtc: string;
    current: AnalyticsPlaybackPerformance;
    comparison: AnalyticsPlaybackPerformance;
}

export interface AnalyticsLocationBucket {
    countryCode?: string | null;
    countryName: string;
    regionName?: string | null;
    city?: string | null;
    latitude: number | null;
    longitude: number | null;
    count: number;
    uniqueUsers: number;
}

export interface AnalyticsDeviceBucket {
    deviceType: string;
    operatingSystem: string;
    operatingSystemVersion?: string | null;
    browser: string;
    browserVersion?: string | null;
    count: number;
    uniqueUsers: number;
}

export interface AnalyticsAudience {
    rangeStartUtc: string;
    rangeEndUtc: string;
    totalLogins: number;
    successfulLogins: number;
    failedLogins: number;
    uniqueUsers: number;
    unknownLocationCount: number;
    locations: AnalyticsLocationBucket[];
    devices: AnalyticsDeviceBucket[];
}

export type AnalyticsWatchStatus = 'Started' | 'InProgress' | 'Completed';

export interface AnalyticsUserWatchHistoryItem {
    videoId: string;
    videoShortId?: string | null;
    videoTitle: string;
    firstWatchedAtUtc: string;
    lastWatchedAtUtc: string;
    watchStartCount: number;
    watchProgressCount: number;
    watchCompleteCount: number;
    lastPositionSeconds?: number | null;
    maxPositionSeconds?: number | null;
    durationSeconds?: number | null;
    progressPercent: number;
    isCompleted: boolean;
    status: AnalyticsWatchStatus;
}

export interface AnalyticsUserWatchHistoryResponse {
    userId: string;
    userName: string;
    email?: string | null;
    rangeStartUtc: string;
    rangeEndUtc: string;
    page: number;
    pageSize: number;
    totalCount: number;
    totalVideos: number;
    completedVideos: number;
    inProgressVideos: number;
    startedOnlyVideos: number;
    averageProgressPercent: number;
    items: AnalyticsUserWatchHistoryItem[];
}

export interface AnalyticsUserWatchEvent {
    occurredAtUtc: string;
    eventType: AnalyticsEventType | string;
    positionSeconds?: number | null;
    durationSeconds?: number | null;
    path?: string | null;
    sessionId?: string | null;
}

export interface AnalyticsUserWatchTimelineResponse {
    userId: string;
    userName: string;
    email?: string | null;
    videoId: string;
    videoShortId?: string | null;
    videoTitle: string;
    rangeStartUtc: string;
    rangeEndUtc: string;
    events: AnalyticsUserWatchEvent[];
}

export interface AnalyticsOverview {
    rangeStartUtc: string;
    rangeEndUtc: string;
    pageViewsSignedIn: AnalyticsDailyCount[];
    pageViewsSignedOut: AnalyticsDailyCount[];
    videoWatchStarts: AnalyticsDailyCount[];
    videoSaves: AnalyticsDailyCount[];
    videoPurchases: AnalyticsDailyCount[];
    signups: AnalyticsDailyCount[];
    subscriptionStarts: AnalyticsDailyCount[];
    subscriptionCancellations: AnalyticsDailyCount[];
    searches: AnalyticsDailyCount[];
    searchResultClicks: AnalyticsDailyCount[];
    recommendationClicks: AnalyticsDailyCount[];
    topWatchedVideos: AnalyticsVideoCount[];
    topSavedVideos: AnalyticsVideoCount[];
    topPurchasedVideos: AnalyticsVideoCount[];
    recentLogins: AnalyticsLoginAudit[];
    recentVideoWatches: AnalyticsVideoAudit[];
    playbackPerformance: AnalyticsPlaybackPerformance;
    eventTypeCounts: AnalyticsEventTypeCount[];
    topSearchTerms: AnalyticsLabelCount[];
    topInterests: AnalyticsLabelCount[];
    topCampaigns: AnalyticsCampaignCount[];
    retention: AnalyticsRetentionSummary;
    recentActivity: AnalyticsRecentActivity[];
    advertisingConversions: AdvertisingConversionSummary[];
}

export interface AnalyticsUserGrowthMonthlyBucket {
    monthStart: string;
    newUsers: number;
    newMembers: number;
    newSubscribed: number;
    newPendingUnsubscribed: number;
    newUnsubscribed: number;
}

export interface AnalyticsUserGrowthTotals {
    totalUsers: number;
    totalEnabledUsers: number;
    totalMembers: number;
    totalSubscribers: number;
    totalPendingUnsubscribed: number;
    totalUnsubscribed: number;
}

export interface AnalyticsUserGrowth {
    rangeStartUtc: string;
    rangeEndUtc: string;
    monthlyGrowth: AnalyticsUserGrowthMonthlyBucket[];
    totals: AnalyticsUserGrowthTotals;
}

export interface SubscriptionFunnelSummary {
    checkoutViews: number;
    eligibleCheckoutViews: number;
    accountsReady: number;
    eligibleAccountsReady: number;
    checkoutsReachingPayment: number;
    completed: number;
    completedWithin24Hours: number;
    knownFailures: number;
    checkoutConversionRate: number;
    accountConversionRate: number;
}

export interface SubscriptionFunnelStage {
    stage: string;
    count: number;
    uniqueUsers: number;
    conversionFromPrevious: number;
}

export interface SubscriptionFunnelFailureReason {
    failureCategory: string;
    count: number;
    percentage: number;
}

export interface SubscriptionFunnelAnalytics {
    rangeStartUtc: string;
    rangeEndUtc: string;
    generatedAtUtc: string;
    completionWindowHours: number;
    summary: SubscriptionFunnelSummary;
    stages: SubscriptionFunnelStage[];
    failureReasons: SubscriptionFunnelFailureReason[];
}
